import { DataStructure } from '@/types/DataStructure'

/**
 * Reads a `#/values/...` reference out of a parsed asset.
 *
 * Values are not kept on the DataStructure itself: they are already resolved
 * into the children of every section, which also keep the original path. So we
 * look the reference up across all sections, ignoring the sections the current
 * user cannot see - the value is the same regardless of who reads it.
 */
export const getAssetValue = (asset: DataStructure | null | undefined, reference: string): string => {
  if (!asset?.nodes || !reference.startsWith('#/values/')) return ''

  const flattenedKey = reference.replace('#/values/', '').replace(/\//g, '.')

  for (const node of Object.values(asset.nodes)) {
    for (const child of node.children || []) {
      if (!child.path || child.value === child.path || child.value === '-') continue

      const childKey = child.path.replace('#/values/', '').replace(/\//g, '.')
      if (child.path === reference || childKey === flattenedKey) {
        return child.value || ''
      }
    }
  }

  return ''
}

export interface WindowRoomAssetInfo {
  positionNumber: string
  floor: string
  description: string
  brand: string
  model: string
  modelDescription: string
  articleDescription: string
  width: string
  height: string
}

/**
 * Everything needed to label a QR tag as the window room it is installed in.
 */
export const getWindowRoomAssetInfo = (asset: DataStructure | null | undefined): WindowRoomAssetInfo => ({
  positionNumber: getAssetValue(asset, '#/values/positionInfo/number'),
  floor: getAssetValue(asset, '#/values/positionInfo/floor'),
  description: getAssetValue(asset, '#/values/positionInfo/description'),
  brand: getAssetValue(asset, '#/values/productDetail/brand'),
  model: getAssetValue(asset, '#/values/productDetail/model'),
  modelDescription: getAssetValue(asset, '#/values/productDetail/description'),
  articleDescription: getAssetValue(asset, '#/values/positionInfo/articleDescription'),
  width: getAssetValue(asset, '#/values/positionInfo/width'),
  height: getAssetValue(asset, '#/values/positionInfo/height')
})
